import React, { Component, useState, useEffect, useRef } from 'react';
import { graphql } from 'gatsby';
import { styled } from 'utils/emotion';
import media from 'utils/mediaqueries';
import Container from 'components/Container';
import Image from 'components/Image';
import Link from 'components/Link';
import Headline from 'components/Headline';
import IconArrowDown from 'components/Svg/IconArrowDown';
import scrollToElement from 'scroll-to-element';
import vhCheck from 'vh-check';
import Redactor from 'components/Redactor';
import { bgGradientBlueOverlay50 } from '../Gradients';

const HeroSectionStyled = styled('section')(
  {
    backgroundSize: 'cover',
    backgroundPosition: 'center top',
    minHeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    position: 'relative',
    zIndex: '0',
    height: 'calc(100vh - 74px)',

    // --vh-offset is from a plugin to take the mobile browser address bar into account
    '@supports(height: calc(100vh - var(--vh-offset, 0px)))': {
      height: 'calc(100vh - var(--vh-offset, 0px) - 74px)',
    },

    [media('lg')]: {
      height: 'calc(100vh - 140px)',

      '@supports(height: calc(100vh - var(--vh-offset, 0px)))': {
        height: 'calc(100vh - var(--vh-offset, 0px) - 140px)',
      },
    },
  },
  props => ({
    backgroundImage: props.backgroundImage,
  }),
);

const HeroContent = styled('div')({
  maxWidth: 1000,
  textAlign: 'center',
  color: 'white',
  padding: '100px 15px 0',
  margin: '0 auto',

  [media('xs', 'sm')]: {
    padding: '0 35px',
  },
  [media('lg')]: {
    width: '500px',
  },
});

const ButtonScrollDown = styled('button')({
  position: 'absolute',
  bottom: 50,
  outline: 'none',
  cursor: 'pointer',
  left: '50%',
  transform: 'translateX(-50%)',
});

export default class HomeHero extends Component {
  componentDidMount() {
    vhCheck();
  }

  render() {
    const {
      headline,
      backgroundImage,
      quote,
      awards,
      linkBox,
    } = this?.props?.homeHero[0];
    const bgImage = backgroundImage[0]
      ? `url(' ${backgroundImage[0].hero} ')`
      : 'none';

    return (
      <HeroSectionStyled
        id="section-1"
        backgroundImage={bgImage}
        className={bgGradientBlueOverlay50}
      >
        {linkBox?.length > 0 && <LinkBox {...linkBox[0]} />}
        <Container>
          <HeroContent>
            {headline && <Headline tag="h1">{headline}</Headline>}
          </HeroContent>
        </Container>
        <ButtonScrollDown
          onClick={() => {
            scrollToElement('#section-2', {
              align: 'top',
              duration: 300,
              offset: -50,
            });
          }}
        >
          <IconArrowDown />
        </ButtonScrollDown>
        <div>
          <AwardSlider awards={awards} />
        </div>

        {quote?.content && <Quote quote={quote} />}
      </HeroSectionStyled>
    );
  }
}

const AwardStyled = styled('img')({
  position: 'absolute',
  top: 10,
  right: 10,
  width: 140,

  [media('md')]: {
    top: 50,
    right: 50,
    width: 200,
  },
});

const AwardSlider = ({ awards }) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const slideInterval = useRef(null);
  const ind = useRef(0);

  const nextSlide = () => {
    ind.current += 1;
    if (ind.current >= awards.length) ind.current = 0;
    setSlideIndex(ind.current);
  };

  useEffect(() => {
    slideInterval.current = setInterval(nextSlide, 3000);

    return () => {
      clearInterval(slideInterval.current);
    };
  }, []);

  return awards.map((file, index) => (
    <AwardStyled
      key={`award-${file.url}`}
      alt="Auszeichnung"
      src={`${file.url}`}
      style={{
        opacity: slideIndex === index ? 1 : 0,
        transition: 'opacity 200ms ease-out',
        transitionDelay: slideIndex === index ? '300ms' : '0ms',
      }}
    />
  ));
};

const LinkBox = ({ text, image, linkUrl }) => (
  <LinkBoxStyled to={linkUrl} target="_blank">
    <Image
      src={image?.[0]?.url}
      alt="link"
      width={image?.[0]?.width}
      height={image?.[0]?.height}
      fluid
      preload
      priority
    />
    <Redactor content={text.content} />
  </LinkBoxStyled>
);

const LinkBoxStyled = styled(Link)({
  position: 'absolute',
  top: 10,
  left: 10,
  width: 300,
  maxWidth: '45%',
  background: 'white',
  padding: 15,
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  border: '4px solid #f0f0f0',
  transition: 'all 200ms ease-out',
  display: 'block',

  '&:hover, &:hover *,&:focus, &:focus *': {
    textDecoration: 'none',
  },

  '&:hover': {
    boxShadow: '0 10px 10px rgba(0,0,0,0.3)',
    borderColor: '#2A6AFA',
  },

  img: { maxWidth: 200 },

  'h2,h3,h4': {
    fontSize: '18px !important',
    marginTop: 15,
  },

  p: {
    margin: '10px 0px 0px',
    fontWeight: 100,
  },

  '*': {
    color: 'black',
    lineHeight: 1.4,
  },

  [media('md')]: {
    top: 50,
    left: 50,
    width: 500,
  },
});

const QuoteStyled = styled('div')({
  position: 'absolute',
  color: 'white',
  maxWidth: '100%',
  padding: '60px 15px',
  textAlign: 'center',
  bottom: 10,

  p: {
    fontSize: 14,
    lineHeight: 1.3,
  },

  [media('md')]: {
    padding: '60px 60px',
    bottom: 0,
    right: 0,
    maxWidth: 500,
    textAlign: 'right',

    p: {
      fontSize: 22,
      lineHeight: 1.4,
    },
  },

  [media('lg')]: {
    textAlign: 'right',
    maxWidth: 800,
  },
});

const Quote = ({ quote }) => (
  <QuoteStyled>
    <Redactor content={quote.content} />
  </QuoteStyled>
);

export const query = graphql`
  fragment HomeHeroQuery on Craft_HomeHeroHero {
    headline
    awards {
      url(transform: transformAward)
    }
    quote {
      content
    }
    backgroundImage {
      url
      hero: url(transform: transformHero)
    }
    linkBox {
      id
      linkUrl
      image {
        url
        full: url(crop: { width: 1920, quality: 90 })
        width
        height
      }
      text {
        totalPages
        content
      }
    }
  }
`;
